import { Container } from '../Container/Container';
import { Heading } from '../Heading/Heading';
import { PreFooterLinksEntry } from './components/PreFooterLinksEntry';
import { twMerge } from 'tailwind-merge';

export interface IlinkListEntry {
  name: string;
  id: string;
  url: string;
  className?: string;
}

interface IlinkList {
  linkList: IlinkListEntry[];
  headingText: string;
}

interface IProps {
  className?: string;
  id?: string;
  linkLists: IlinkList[];
  title?: string;
}

/**
 * Component to render a pre-footer section with multiple lists of links.
 *
 * @param {IProps} props - The properties for the PreFooterLinks component.
 * @param {string} [props.className] - Optional additional class names to apply to the container.
 * @param {string} [props.id='explore-more'] - Optional ID for the container element.
 * @param {IlinkList[]} props.linkLists - Array of link lists to be displayed in the pre-footer.
 * @param {string} [props.title='Explore more'] - Optional title for the pre-footer section.
 *
 * @returns {JSX.Element | null} The rendered pre-footer links component or null if no link lists are provided.
 */
export const PreFooterLinks = ({
  className,
  id = 'explore-more',
  linkLists,
  title = 'Explore more',
}: IProps) => {
  if (linkLists.length === 0) {
    return null;
  }
  return (
    <div
      id={id}
      className="bg-lake-fog w-full scroll-mt-(--navigation-scrolltop-offset-mobile) py-10 sm:scroll-mt-(--navigation-scrolltop-offset)"
    >
      <Container className="flex flex-col gap-6 sm:gap-10">
        <Heading as="span" variant="sm">
          {title}
        </Heading>
        <div className={twMerge(`flex flex-col gap-x-6 md:flex-row`, className)}>
          {linkLists.map(({ linkList, headingText }, index) => (
            <PreFooterLinksEntry key={index} linkList={linkList} headingText={headingText} />
          ))}
        </div>
      </Container>
    </div>
  );
};
